import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CrudService } from '../crud.service';
import {
  KeyValueDistricts,
  FeatureProperties,
  GeoData,
  FeatureCollection,
} from '../../models/geo-data';
import { POI } from '../../models/data-layer-type';
import { LeafletMapboxService } from '../leaflet-mapbox.service';
import { concatMap, filter, from, map, Observable, of } from 'rxjs';
import { saveAs } from 'file-saver';
import { GeoJSON } from 'leaflet';

@Injectable({
  providedIn: 'root',
})
export class GeoDataService extends CrudService<GeoData> {
  protected basePath = 'geoData'; // Specific for GeoDataService
  public instanceType = 'geoData'; // Used in base service for constructing URLs
  private defaultProjection = 'EPSG:4326';

  constructor(
    http: HttpClient,
    private mapBoxService: LeafletMapboxService
  ) {
    super(http);
  }

  findMany(filterByKeys: string[]) {
    const headers = new HttpHeaders({
      'X-Filter-Keys': filterByKeys ? JSON.stringify(filterByKeys) : '',
    });

    return this.http.get<{ [key: string]: GeoData }>(
      `${this.getBaseUrl()}/commune/${this.userStore.commune()}`,
      {
        headers: headers,
      }
    );
  }

  /**
   * Constructs and returns the full import URL for converting geo data to GeoJSON.
   *
   * @return {string} The complete URL for the geo data to GeoJSON conversion endpoint.
   */
  getImportUrl(): string {
    return `${this.getBaseUrl()}/convertGeoDataToGeoJson`;
  }

  /**
   * Changes the projection of the given file by sending a request to the server.
   *
   * @param {any} file The file object containing details such as its path.
   * @param {string} projection The target projection to which the file should be converted.
   * @return {Observable<any>} An Observable that emits the server's response after processing the projection change.
   */
  changeProjection(file: any, projection: string): Observable<any> {
    return this.http.post(`${this.getBaseUrl()}/changeProjection`, {
      filePath: file.path,
      projection: projection,
    });
  }

  /**
   * Constructs and returns the URL for importing Points of Interest (POI) by converting an XLSX file to GeoJSON format.
   *
   * @return {string} The URL for the POI import process.
   */
  getPoiImportUrl(): string {
    return `${this.getBaseUrl()}/convertXlsxToGeoJson`;
  }

  /**
   * Retrieves the default projection used by the instance.
   *
   * @return {Object} The default projection of the instance.
   */
  getDefaultProjection(): string {
    return this.defaultProjection;
  }

  /**
   * Generates an empty GeoData object structured from the input districts and geometry type.
   *
   * @param {KeyValueDistricts} districts - An object containing district keys mapped to district properties.
   * @param {'Polygon' | 'Point' | 'POI'} type - The geometry type for the GeoJSON features, either 'Polygon', 'Point' or 'POI'.
   * @return {GeoData} A GeoData object with an empty or placeholder structure based on the provided districts and type.
   */
  generateEmptyGeoData(
    districts: KeyValueDistricts,
    type: 'Polygon' | 'Point'
  ): GeoData {
    const features: GeoJSON.Feature[] = [];
    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: features,
    };
    const geoData: GeoData = {
      _id: '',
      geoJson: '',
      poiJson: false,
    };

    for (const districtKey in districts) {
      features.push({
        type: 'Feature',
        properties: {
          key: districtKey,
          name: districts[districtKey].name,
        },
        geometry: {
          coordinates: [],
          type: type === 'Point' ? 'Point' : type,
        },
      });
    }
    geoData.geoJson = JSON.stringify(collection);
    return geoData;
  }

  /**
   * Generates a GeoData object containing a FeatureCollection derived from the provided features.
   *
   * @param {GeoJSON.Feature[]} features - The array of GeoJSON Features to include in the resulting FeatureCollection.
   * @param {GeoJSON.Feature[]} [currentFeatures] - An optional array of GeoJSON Features to merge with the provided features. If provided, properties from matching features will be merged.
   * @param {string} [group] - An optional group name to set on the FeatureCollection.
   * @return {GeoData} A GeoData object containing the generated FeatureCollection as a JSON string.
   */
  generateByFeatures(
    features: GeoJSON.Feature[],
    currentFeatures?: GeoJSON.Feature[],
    group?: string
  ): GeoData {
    const collection: FeatureCollection = {
      type: 'FeatureCollection',
      features: currentFeatures
        ? features.map((feature, i) => {
            feature.properties = {
              ...(currentFeatures[i]?.properties ?? {}),
              ...feature.properties,
            };
            return feature;
          })
        : features,
      group: group,
    };
    return {
      _id: '',
      geoJson: JSON.stringify(collection),
      poiJson: false,
    };
  }

  /**
   * Convert addresses to coordinates for POIs lacking geoPosition.
   * @returns A promise that resolves when all conversions are complete.
   */
  convertAddressesToCoordinates(pois: POI[]): Observable<void> {
    const conversionObservables = pois.map((poi, index) =>
      of(poi).pipe(
        concatMap((poi: POI) => {
          if (poi.address && (!poi.geoPositionX || !poi.geoPositionY)) {
            return this.convertOneAddressToCoordinate(poi.address, index).pipe(
              map((res: any) => {
                const coordinates = res.coordinates;
                poi.geoPositionX = coordinates[0];
                poi.geoPositionY = coordinates[1];
                poi.projection = this.getDefaultProjection();
              }),
              map(() => void 0) // Ensure the observable emits void
            );
          } else {
            return of(void 0);
          }
        })
      )
    );

    return from(conversionObservables).pipe(
      concatMap(obs => obs),
      filter(result => result === undefined)
    );
  }

  /**
   * Converts a given address to geographical coordinates using a specific API endpoint.
   *
   * @param {string} address The address to be converted into coordinates.
   * @param {number} index An associated index for the address, typically used to track or identify the request.
   * @return {Observable<any>} An observable containing the response from the API, which includes the coordinate data corresponding to the address.
   */
  convertOneAddressToCoordinate(
    address: string,
    index: number
  ): Observable<any> {
    return this.http.post(`${this.getBaseUrl()}/convertAddressToCoordinates`, {
      accessKey: this.mapBoxService.getMapBoxToken(),
      address: address,
      index: index,
    });
  }

  /**
   * Converts an array of points of interest (POI) into GeoJSON Feature objects.
   *
   * @param {POI[]} pois - An array of POI objects, each containing geographical and property information.
   * @return {GeoData} - A GeoJSON object generated from the provided POI data.
   */
  convertPoiToGeoData(pois: POI[]): GeoData {
    const features: GeoJSON.Feature[] = [];
    const propertyList: Array<keyof FeatureProperties> = [
      'key',
      'name',
      'address',
      'description',
      'contact',
      'information',
      'group',
    ];

    pois.forEach(function (poi) {
      if (!poi.geoPositionX || !poi.geoPositionY) return;
      const properties: FeatureProperties = {
        key: '',
        name: '',
      };

      for (const property of propertyList) {
        if (poi[property]) {
          const value = String(poi[property]);
          properties[property] = value.trim();
        } else {
          properties[property] = '';
        }
      }

      const feature: GeoJSON.Feature<GeoJSON.Point | GeoJSON.Polygon> = {
        properties: properties,
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [poi.geoPositionX, poi.geoPositionY],
        },
      };

      features.push(feature);
    });
    return this.generateByFeatures(features);
  }

  /**
   * download geo-data as json-file using file-saver
   */
  downloadGeoJson(
    filename: string,
    json: GeoJSON.FeatureCollection<
      GeoJSON.Point | GeoJSON.Polygon,
      GeoJSON.GeoJsonProperties
    > | null
  ) {
    if (json) {
      // Create a blob of the data
      const fileToSave = new Blob([JSON.stringify(json)], {
        type: 'application/json;charset=utf-8',
      });

      // Save the file
      saveAs(fileToSave, filename);
    }
  }

  /**
   * Converts a given 2D array of strings to an XLSX file by sending it to the server.
   *
   * @param {string[][]} featureArray - The 2D array of strings representing the data to be converted to XLSX format.
   * @param {string} filename - the name of the download-file
   * @return {Observable<Blob>} An observable that emits the binary data of the generated XLSX file.
   */
  convertToXlsx(featureArray: string[][], filename: string): Observable<Blob> {
    return this.http.post(
      `${this.getBaseUrl()}/convertJsonToXlsx`,
      { featureArray, filename },
      {
        responseType: 'blob',
      }
    );
  }
}
